import React, { createContext, useContext, useState, useEffect } from 'react';

export type Currency = 'GBP' | 'USD' | 'EUR' | 'AUD' | 'INR';

interface CurrencyContextType {
    currency: Currency;
    setCurrency: (c: Currency) => void;
    showBanner: boolean;
    setShowBanner: (show: boolean) => void;
    isLoading: boolean;
    suggestedCurrency?: Currency;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
    const [currency, setCurrency] = useState<Currency>('GBP');
    const [suggestedCurrency, setSuggestedCurrency] = useState<Currency | undefined>(undefined);
    const [showBanner, setShowBanner] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const checkLocation = async () => {
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();

                let detected: Currency | undefined;

                // Direct Currency Match
                if (['USD', 'EUR', 'AUD', 'INR'].includes(data.currency)) {
                    detected = data.currency as Currency;
                }
                // Specific Mappings
                else if (data.currency === 'NZD') {
                    detected = 'AUD';
                }

                if (detected && detected !== 'GBP') {
                    setSuggestedCurrency(detected);
                    setShowBanner(true);
                }

            } catch (error) {
                console.warn('Failed to detect location for currency:', error);
            } finally {
                setIsLoading(false);
            }
        };

        checkLocation();
    }, []);

    return (
        <CurrencyContext.Provider
            value={{
                currency,
                setCurrency,
                showBanner,
                setShowBanner,
                isLoading,
                suggestedCurrency
            }}
        >
            {children}
        </CurrencyContext.Provider>
    );
}

export function useCurrency() {
    const context = useContext(CurrencyContext);
    if (context === undefined) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
}
